import { useEffect, useState } from "react";
import { PUBLIC_TRIAL_SECONDS } from "@/app/publicTrialPolicy";

/** Wall time, including background/suspended time. A credential refresh cannot
 * extend the same surface; a different selection starts a separate attempt. */
export function usePublicTrialClock(key: string, active: boolean, expiresAt?: string, limitSeconds: number | null = PUBLIC_TRIAL_SECONDS) {
  const [attempt, setAttempt] = useState({ key, limitSeconds, deadline: active ? limitSeconds === null ? Infinity : Date.now() + limitSeconds * 1_000 : null });
  if (attempt.key !== key || active && attempt.deadline === null) {
    setAttempt({ key, limitSeconds, deadline: active ? limitSeconds === null ? Infinity : Date.now() + limitSeconds * 1_000 : null });
  }
  const localDeadline = attempt.key === key ? attempt.deadline ?? Infinity : Infinity;
  const serverDeadline = attempt.limitSeconds === null || expiresAt === undefined ? Infinity : Date.parse(expiresAt);
  if (active && attempt.key === key && attempt.deadline !== null &&
      Number.isFinite(serverDeadline) && serverDeadline < attempt.deadline) {
    setAttempt({ ...attempt, deadline: serverDeadline });
  }
  const deadline = Math.min(localDeadline, serverDeadline);
  const [now, setNow] = useState(Date.now);
  useEffect(() => {
    if (!active || attempt.limitSeconds === null) return;
    const tick = () => setNow(Date.now());
    tick();
    const timer = setInterval(tick, 250);
    window.addEventListener("focus", tick);
    document.addEventListener("visibilitychange", tick);
    return () => {
      clearInterval(timer);
      window.removeEventListener("focus", tick);
      document.removeEventListener("visibilitychange", tick);
    };
  }, [active, key, attempt.limitSeconds]);
  return {
    limitSeconds: attempt.limitSeconds,
    expiresAt: attempt.limitSeconds === null ? null : new Date(Number.isFinite(deadline) ? deadline : 0).toISOString(),
    expired: active && attempt.limitSeconds !== null && (!Number.isFinite(deadline) || Math.max(now, Date.now()) >= deadline),
    remainingSeconds: active && attempt.limitSeconds !== null ? Math.max(0, Math.ceil((deadline - now) / 1_000)) : null,
  };
}
